import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CustomPagination from '../../../components/customPagination';
import { useGetMetersHandler } from '../dataHandlers/getMetersHandler';
import MeterActionMenu from './MeterActionMenu';

export default function MetersTable({ handlePaginationChange, searchQuery = {} }) {
  const { meters, totalDocuments, isLoading } = useGetMetersHandler({ searchQuery });

  const handleCopyMeterId = async (meterId) => {
    if (!meterId) return;

    try {
      await navigator.clipboard.writeText(meterId);
    } catch (error) {
      // Ignore clipboard failures and keep table interactions uninterrupted.
    }
  };

  const columns = [
    {
      field: 'meter_id',
      headerName: 'Meter ID',
      flex: 1,
      renderCell: (params) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%', height: '100%' }}
        >
          <Typography
            variant="body2"
            title={params.value}
            sx={{
              minWidth: 0,
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </Typography>
          <Tooltip title="Copy Meter ID">
            <IconButton
              size="small"
              color="primary"
              sx={{ ml: 0.5, flexShrink: 0 }}
              aria-label={`copy-${params.value}`}
              onClick={(event) => {
                event.stopPropagation();
                handleCopyMeterId(params.value);
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    { field: 'display_name', headerName: 'Display Name', flex: 1 },
    { field: 'event_name', headerName: 'Event Name', flex: 1 },
    {
      field: 'active',
      headerName: 'Status',
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    { field: 'aggregation_formula', headerName: 'Aggregation', flex: 0.8 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: (params) => <MeterActionMenu meter={params.row} />,
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={meters}
        columns={columns}
        getRowId={(row) => row.meter_id}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10, 25]}
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => handlePaginationChange(model.page, model.pageSize)}
        slots={{ pagination: CustomPagination }}
      />
    </Box>
  );
}
